"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComponentState = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importDefault(require("react"));
const react_native_1 = require("react-native");
const hooks_1 = require("../../hooks");
const defaultStyles = (theme) => ({
    actionRoot: {
        marginTop: theme.spacing.unit,
    },
    description: {
        marginBottom: theme.spacing.unit,
        textAlign: 'center',
    },
    image: {
        height: 250,
        width: 250,
    },
    imageRoot: {
        marginBottom: theme.spacing.unit * 2,
    },
    root: {
        alignItems: 'center',
        flexGrow: 1,
        justifyContent: 'center',
        padding: theme.spacing.unit * 2,
    },
    title: {
        marginBottom: theme.spacing.unit,
        textAlign: 'center',
    },
});
/**
 * # 🤡 ComponentState
 *
 * A generic component to show different states of a screen or a view. For example,
 * you may need to:
 *
 * - Show a loading state when data is loading,
 * - Show an empty state when there is not data to show on a screen.
 * - Show an error message when an exception occurs during execution.
 *
 * These are just a few examples. This component displays a message with an image, a title,
 * a description and a call to action button.
 *
 * ## Usage
 * ```jsx
 * <ComponentState
 *  title="Looks like your'e new here!"
 *  description="Start by creating your first entry."
 *  imageSource="https://picsum.photos/200"
 *  styles={{ image: { width: 100, height: 100 } }}
 *  actionTitle="Tap to Create"
 * />
 * ```
 */
const ComponentState = (props) => {
    const { actionOnPress, actionProps: _actionProps = {}, actionTitle, description, descriptionProps: _descriptionProps = {}, image, imageProps: _imageProps = {}, imageSource, title, titleProps: _titleProps = {}, } = props;
    const BlueBaseImage = hooks_1.useComponent('BlueBaseImage');
    const Button = hooks_1.useComponent('Button');
    const Body2 = hooks_1.useComponent('Body2');
    const FormattedMessage = hooks_1.useComponent('FormattedMessage');
    const H6 = hooks_1.useComponent('H6');
    const View = hooks_1.useComponent('View');
    const styles = hooks_1.useStyles('ComponentState', props, defaultStyles);
    const actionProps = Object.assign(Object.assign({ color: 'primary' }, _actionProps), { component: Button, onPress: actionOnPress, style: react_native_1.StyleSheet.flatten([styles.action, _actionProps.style]) });
    const descriptionProps = Object.assign(Object.assign({}, _descriptionProps), { children: description, component: Body2, style: react_native_1.StyleSheet.flatten([styles.description, _descriptionProps.style]) });
    const titleProps = Object.assign(Object.assign({}, _titleProps), { children: title, component: H6, style: react_native_1.StyleSheet.flatten([styles.title, _titleProps.style]) });
    const imageProps = Object.assign(Object.assign({}, _imageProps), { source: imageSource || _imageProps.source, style: react_native_1.StyleSheet.flatten([styles.image, _imageProps.style]) });
    return (react_1.default.createElement(View, { style: styles.root },
        image ? (react_1.default.createElement(View, { style: styles.imageRoot }, image)) : imageSource ? (react_1.default.createElement(View, { style: styles.imageRoot },
            react_1.default.createElement(BlueBaseImage, Object.assign({}, imageProps)))) : null,
        title ? react_1.default.createElement(FormattedMessage, Object.assign({}, titleProps)) : null,
        description ? react_1.default.createElement(FormattedMessage, Object.assign({}, descriptionProps)) : null,
        actionTitle ? (react_1.default.createElement(View, { style: styles.actionRoot },
            react_1.default.createElement(FormattedMessage, Object.assign({}, actionProps), actionTitle))) : null));
};
exports.ComponentState = ComponentState;
exports.ComponentState.displayName = 'ComponentState';
